// apps/web/src/components/Security/TwoFactorChallenge.web.jsx
import React, { useMemo, useState, useEffect } from 'react';
import api from '@mysimpledb/shared/config/axios';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { finishLogin } from '@mysimpledb/shared/data/auth/finishLogin';
import { toast } from 'react-hot-toast';

// Helper to read token from different places
function useChallengeToken(explicitToken) {
  const [search] = useSearchParams();
  const location = useLocation();

  // priority: prop -> location.state.challengeToken -> ?token=...
  const tokenFromState = location?.state?.challengeToken;
  const tokenFromQuery = search.get('token');

  return explicitToken || tokenFromState || tokenFromQuery || null;
}

export default function TwoFactorChallengeWeb({
  challengeToken: tokenProp,
  setUserId,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location?.state?.email || '';

  const challengeToken = useChallengeToken(tokenProp);

  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [loading, setLoading] = useState(false);

  const valid = useMemo(() => /^\d{6}$/.test(code), [code]);

  useEffect(() => {
    if (!challengeToken) {
      toast.error('Missing 2FA challenge token. Please sign in again.');
    }
  }, [challengeToken]);

  const handleChange = (e) => {
    // allow partial typing; digits only; cap at 6
    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(v);
  };

  async function submit(e) {
    e?.preventDefault();
    if (!challengeToken || !valid || loading) return;

    setLoading(true);
    try {
      // Verify and request a REAL session token
      const { data } = await api.post(
        '/twofa/challenge',
        { code: code.trim(), rememberDevice },
        { headers: { Authorization: `Bearer ${challengeToken}` } }
      );

      // Server should return { token, userId }
      if (!data?.token || !data?.userId || !data?.productCode) {
        throw new Error('Invalid response from server.');
      }

      await finishLogin(
        {
          token: data.token,
          userId: data.userId,
          email,
          productCode: data.productCode,
        },
        { navigate, setUserId }
      );

      toast.success('Verification successful');
    } catch (err) {
      const msg =
        err?.response?.data?.error ?? err?.message ?? 'Could not verify';
      toast.error(msg === 'invalid_code' ? 'Invalid code' : msg);
      setLoading(false);
    }
  }

  // simple styles to match your app’s cards
  const card = {
    maxWidth: 460,
    margin: '24px auto',
    background: '#fff',
    borderRadius: 14,
    padding: 20,
    boxShadow: '0 1px 2px rgba(0,0,0,.05)',
    border: '1px solid rgba(0,0,0,.06)',
    position: 'relative',
    zIndex: 10,
  };

  const input = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 10,
    border: '1px solid #d1d5db',
    fontSize: 22,
    letterSpacing: 4,
    textAlign: 'center',
  };

  const btn = {
    padding: '10px 14px',
    borderRadius: 10,
    border: 0,
    color: '#fff',
    opacity: valid && !loading ? 1 : 0.3,
    background: '#081e4d',
    cursor: valid && !loading ? 'pointer' : 'not-allowed',
    width: '100%',
    marginTop: 12,
  };

  const rememberRow = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
    userSelect: 'none',
  };

  return (
    <div style={card} onClick={(e) => e.stopPropagation()}>
      <h2 style={{ marginTop: 0 }}>Enter 6-digit code</h2>
      <p style={{ marginTop: 4, color: '#081e4d' }}>
        Open your authenticator app and enter the code.
      </p>

      <form onSubmit={submit}>
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder="123456"
          autoFocus
          value={code}
          onChange={handleChange}
          onKeyDown={(e) => e.stopPropagation()}
          style={input}
        />

        <label style={rememberRow}>
          <input
            type="checkbox"
            checked={rememberDevice}
            onChange={(e) => setRememberDevice(e.target.checked)}
          />
          Remember this device for 30 days
        </label>

        <button
          type="submit"
          style={btn}
          disabled={!valid || loading || !challengeToken}
        >
          {loading ? 'Verifying…' : 'Verify'}
        </button>
      </form>
    </div>
  );
}
